// Брони: шахматка столы × часы. Блок — бронь; клик по блоку — действия,
// «+ Бронь» — форма. Конфликт времени по одному столу отбивает сервер.
import { useMemo, useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { downloadCsv } from './csv';
import { formatMoney, formatPhoneKz } from '@dastarhan/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HallMap, LocationsList, ReservationsResponse, type ReservationRow , ReservationRequestsList } from '@dastarhan/contracts';

const API = '/api/v1';
const DAY_START = 11 * 60;
const DAY_END = 23 * 60;
const SPAN = DAY_END - DAY_START;
const HOURS = Array.from({ length: (DAY_END - DAY_START) / 60 }, (_, i) => DAY_START / 60 + i);

const STATUS_RU: Record<ReservationRow['status'], string> = {
  PLANNED: 'ждём', SEATED: 'сел', CANCELLED: 'отмена', NO_SHOW: 'не пришёл',
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ReservationsScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const [locationId, setLocationId] = useState<string | null>(null);
  const loc = locationId ?? locations.data?.[0]?.id ?? null;
  const [date, setDate] = useState(todayStr());

  const halls = useQuery({
    queryKey: ['halls-map', loc],
    enabled: !!loc,
    queryFn: async () => HallMap.parse(await (await fetch(`${API}/hall/map?locationId=${loc}`, { headers: h })).json()),
  });
  const resv = useQuery({
    queryKey: ['resv', loc, date],
    enabled: !!loc,
    refetchInterval: 30_000,
    queryFn: async () =>
      ReservationsResponse.parse(await (await fetch(`${API}/reservations?locationId=${loc}&date=${date}`, { headers: h })).json()),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['resv'] });

  const requests = useQuery({
    queryKey: ['resv-requests'],
    refetchInterval: 30_000,
    queryFn: async () => ReservationRequestsList.parse(
      await (await fetch(`${API}/reservations/requests`, { headers: h })).json(),
    ),
  });
  const [approving, setApproving] = useState<ReservationRequestsList['rows'][number] | null>(null);
  const [apr, setApr] = useState({ tableId: '', time: '', durationMin: '120' });
  const actRequest = useMutation({
    mutationFn: (b: { id: string; action: 'approve' | 'decline'; body?: object }) =>
      fetch(`${API}/reservations/requests/${b.id}/${b.action}`, {
        method: 'POST', headers: jh,
        ...(b.body ? { body: JSON.stringify(b.body) } : {}),
      }).then(async (r) => { if (!r.ok) throw new Error(((await r.json()) as { code?: string }).code ?? 'ERR'); }),
    onSuccess: () => {
      setApproving(null);
      void qc.invalidateQueries({ queryKey: ['resv-requests'] });
      void qc.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  const create = useMutation({
    mutationFn: (b: object) =>
      fetch(`${API}/reservations`, { method: 'POST', headers: jh, body: JSON.stringify(b) })
        .then(async (r) => { if (!r.ok) throw new Error((await r.json()).code ?? 'ERR'); }),
    onSuccess: refresh,
  });
  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: string; tableId?: string; status?: ReservationRow['status'] }) =>
      fetch(`${API}/reservations/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(body) }),
    onSuccess: refresh,
  });

  const tables = useMemo(
    () => (halls.data ?? []).flatMap((hl) => hl.tables.map((t) => ({ ...t, hall: hl.name }))),
    [halls.data],
  );
  const byTable = useMemo(() => {
    const m = new Map<string, ReservationRow[]>();
    for (const r of resv.data?.rows ?? []) {
      m.set(r.tableId, [...(m.get(r.tableId) ?? []), r]);
    }
    return m;
  }, [resv.data]);

  const [openResv, setOpenResv] = useState<ReservationRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ tableId: '', guestName: '', phone: '', guests: '2', time: '19:00', durationMin: '120', deposit: '' });

  if (locations.isPending || halls.isPending) return <p className="empty">Загружаем…</p>;
  if (!loc) return <p className="empty">Сначала нужна точка.</p>;
  if (!tables.length) return <p className="empty">Столов ещё нет — расставьте их во вкладке «Зал», и шахматка появится здесь.</p>;

  const conflict = create.isError ? (create.error as Error).message : null;

  return (
    <div className="resv-screen">
      <div className="menu-toolbar">
        <div className="row">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <button className="btn" onClick={() => setDate(todayStr())}>Сегодня</button>
        </div>
        <button className="btn accent" onClick={() => {
          setForm({ tableId: tables[0]!.id, guestName: '', phone: '', guests: '2', time: '19:00', durationMin: '120', deposit: '' });
          create.reset();
          setCreating(true);
        }}>+ Бронь</button>
      </div>

      {(requests.data?.rows.length ?? 0) > 0 && (
        <section className="menu-cat" style={{ marginBottom: 14 }}>
          <header>
            <h2>Заявки с витрины</h2>
            <span className="rule-tag">{requests.data!.rows.length}</span>
            <button className="btn small" style={{ marginLeft: 'auto' }} onClick={() => {
              downloadCsv('заявки_с_витрины.csv', [
                ['Имя', 'Телефон', 'Гостей', 'Желаемое время', 'Создана'],
                ...requests.data!.rows.map((rq) => [
                  rq.guestName, formatPhoneKz(rq.phone), rq.guests,
                  new Date(rq.wishAt).toLocaleString('ru-RU'),
                  new Date(rq.createdAt).toLocaleString('ru-RU'),
                ]),
              ]);
            }}>CSV</button>
          </header>
          {requests.data!.rows.map((rq) => (
            <div key={rq.id} className="menu-row req-row">
              <span className="name">
                {rq.guestName} · <a href={`tel:${rq.phone.replace(/[^+\d]/g, '')}`}>
                  📞 {formatPhoneKz(rq.phone)}
                </a>
                <small className="hint-inline"> · {rq.guests} гост.</small>
              </span>
              <span className="hint-inline">
                {new Date(rq.wishAt).toLocaleString('ru-RU', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })}
              </span>
              <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                <button className="btn small" onClick={() => actRequest.mutate({ id: rq.id, action: 'decline' })}>
                  Отклонить
                </button>
                <button className="btn accent small" onClick={() => {
                  const w = new Date(rq.wishAt);
                  setApr({
                    tableId: tables[0]?.id ?? '',
                    time: `${String(w.getHours()).padStart(2, '0')}:${String(w.getMinutes()).padStart(2, '0')}`,
                    durationMin: '120',
                  });
                  actRequest.reset();
                  setApproving(rq);
                }}>Подтвердить</button>
              </span>
            </div>
          ))}
        </section>
      )}

      {approving && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setApproving(null)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Стол для {approving.guestName}</h2>
            <p className="hint-inline" style={{ margin: 0 }}>
              Гость просил {new Date(approving.wishAt).toLocaleString('ru-RU', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })} · {approving.guests} гост. SMS-подтверждение уйдёт сам.
            </p>
            <label>Стол
              <select value={apr.tableId} onChange={(e) => setApr({ ...apr, tableId: e.target.value })}>
                {tables.map((t) => <option key={t.id} value={t.id}>{t.hall} · {t.name}</option>)}
              </select>
            </label>
            <div className="row" style={{ gap: 8 }}>
              <label style={{ flex: 1 }}>Время
                <input type="time" value={apr.time}
                  onChange={(e) => setApr({ ...apr, time: e.target.value })} />
              </label>
              <label style={{ flex: 1 }}>Длительность
                <select value={apr.durationMin}
                  onChange={(e) => setApr({ ...apr, durationMin: e.target.value })}>
                  <option value="60">1 час</option>
                  <option value="90">1.5 часа</option>
                  <option value="120">2 часа</option>
                  <option value="180">3 часа</option>
                </select>
              </label>
            </div>
            {actRequest.isError && (
              <p className="dlg-err">
                {(actRequest.error as Error).message === 'TABLE_BUSY'
                  ? 'Стол в это время занят — выберите другой стол или время.'
                  : 'Не получилось подтвердить. Попробуйте ещё раз.'}
              </p>
            )}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setApproving(null)}>Отмена</button>
              <button className="btn accent" disabled={!apr.tableId || actRequest.isPending}
                onClick={() => {
                  const base = new Date(approving.wishAt);
                  const [hh2, mm2] = apr.time.split(':').map(Number);
                  base.setHours(hh2 ?? 19, mm2 ?? 0, 0, 0);
                  actRequest.mutate({
                    id: approving.id,
                    action: 'approve',
                    body: {
                      tableId: apr.tableId,
                      startAt: base.toISOString(),
                      durationMin: Number(apr.durationMin),
                    },
                  });
                }}>Подтвердить бронь</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid-wrap">
        <div className="grid-head">
          <div className="grid-corner" />
          {HOURS.map((hh) => <div key={hh} className="grid-hour">{hh}:00</div>)}
        </div>
        {tables.map((t) => (
          <div key={t.id} className="grid-row">
            <div className="grid-table"><b>{t.name}</b><span>{t.seats} мест</span></div>
            <div className="grid-track">
              {HOURS.map((hh) => <i key={hh} className="grid-cell" />)}
              {(byTable.get(t.id) ?? []).map((r) => {
                const st = new Date(r.startAt);
                const startMin = st.getHours() * 60 + st.getMinutes();
                const left = Math.max(0, ((startMin - DAY_START) / SPAN) * 100);
                const width = Math.min(100 - left, (r.durationMin / SPAN) * 100);
                return (
                  <button key={r.id}
                    className={`resv-block s-${r.status}`}
                    style={{ left: `${left}%`, width: `${width}%` }}
                    title={`${r.guestName} · ${r.guests} чел · ${STATUS_RU[r.status]}${r.deposit > 0 ? ` · депозит ${formatMoney(r.deposit)}${r.depositUsedAt ? ' ✓' : ''}` : ''}`}
                    onClick={() => setOpenResv(r)}>
                    {r.guestName}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {openResv && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setOpenResv(null)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{openResv.tableName} · {new Date(openResv.startAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</h2>
            <p className="dlg-line"><b>{openResv.guestName}</b> · {openResv.guests} чел
              {openResv.phone && <> · {openResv.phone}</>}</p>
            <p className="dlg-line">Статус: {STATUS_RU[openResv.status]}{openResv.comment && <> · {openResv.comment}</>}</p>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              {openResv.status === 'PLANNED' && (
                <>
                  <button className="btn" onClick={() => { patch.mutate({ id: openResv.id, status: 'SEATED' }); setOpenResv(null); }}>Гость сел</button>
                  <button className="btn" onClick={() => { patch.mutate({ id: openResv.id, status: 'NO_SHOW' }); setOpenResv(null); }}>Не пришёл</button>
                </>
              )}
              {(openResv.status === 'PLANNED' || openResv.status === 'SEATED') && (
                <>
                  <button className="btn" title="Перенести бронь на другой стол"
                    onClick={() => void (async () => {
                      /* Стол выбирают из списка: набранное руками имя не
                         совпадало по букве, и бронь оставалась на прежнем. */
                      const a = await ask({
                        title: 'Перенести бронь на другой стол',
                        sub: `${openResv.guestName} · ${openResv.guests} чел · ${openResv.tableName}`,
                        choices: {
                          label: 'Куда переезжаем',
                          initial: openResv.tableId ?? undefined,
                          options: tables.map((x) => ({
                            id: x.id,
                            title: x.name,
                            note: x.id === openResv.tableId ? 'сейчас здесь' : `${x.seats} мест`,
                          })),
                        },
                        confirmLabel: 'Перенести',
                      });
                      if (!a) return;
                      const t = tables.find((x) => x.id === a.choice);
                      if (!t) return;
                      patch.mutate({ id: openResv.id, tableId: t.id });
                      setOpenResv(null);
                      note(`Бронь перенесена на «${t.name}»`);
                    })()}>⇄ Стол</button>
                  <button className="btn danger-btn" onClick={() => { patch.mutate({ id: openResv.id, status: 'CANCELLED' }); setOpenResv(null); }}>Отменить</button>
                </>
              )}
              <button className="btn" onClick={() => setOpenResv(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setCreating(false)}>
          <form className="office-dialog" onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.guestName.trim()) return;
              create.mutate({
                locationId: loc,
                tableId: form.tableId,
                guestName: form.guestName.trim(),
                ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
                guests: Math.max(1, Number(form.guests) || 2),
                startAt: new Date(`${date}T${form.time}:00`).toISOString(),
                durationMin: Number(form.durationMin),
          ...(form.deposit ? { deposit: Number(form.deposit) * 100 } : {}),
              }, { onSuccess: () => setCreating(false) });
            }}>
            <h2>Новая бронь · {date}</h2>
            <label>Стол
              <select value={form.tableId} onChange={(e) => setForm({ ...form, tableId: e.target.value })}>
                {tables.map((t) => <option key={t.id} value={t.id}>{t.hall} · {t.name} ({t.seats})</option>)}
              </select>
            </label>
            <div className="row">
              <label>Время <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></label>
              <label>Часа
                <select value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: e.target.value })}>
                  <option value="60">1</option><option value="90">1.5</option>
                  <option value="120">2</option><option value="180">3</option>
                </select>
              </label>
              <label>Гостей <input inputMode="numeric" style={{ width: 60 }} value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })} /></label>
            <label>Депозит, ₸
              <input inputMode="numeric" placeholder="0" value={form.deposit}
                onChange={(e) => setForm({ ...form, deposit: e.target.value.replace(/\D/g, '') })} />
            </label>
            </div>
            <label>Имя гостя <input autoFocus value={form.guestName}
              onChange={(e) => setForm({ ...form, guestName: e.target.value })} /></label>
            <label>Телефон <input value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
            {conflict === 'TABLE_BUSY' && <p className="dlg-err">Стол в это время уже занят другой бронью.</p>}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" type="button" onClick={() => setCreating(false)}>Отмена</button>
              <button className="btn accent" type="submit" disabled={!form.guestName.trim() || create.isPending}>Создать</button>
            </div>
          </form>
        </div>
      )}
      {sheet}
      {noteView}
    </div>
  );
}
